import { useDepositById, useWithdrawalById } from '@starsoft/common/api';
import { useSearchParams } from '../../useSearchParams';
import { parseToBigInt } from '@starsoft/common/utils/parsers';

/**
 * Custom hook to fetch transaction data by ID.
 * This hook retrieves deposit or withdrawal data based on URL search parameters.
 *
 * @returns {Object} - An object containing the transaction data, error, loading state, and a refetch function.
 *
 * Example usage:
 * const { error, refetch, isLoading, transaction } = useTransactionById();
 */
export function useTransactionById() {
  // Retrieve URL search parameters
  const search: URLSearchParams = useSearchParams();

  // Extract deposit and withdrawal IDs from search parameters
  const depositId = search.get('deposit-id');
  const withdrawId = search.get('withdraw-id');

  // Fetch deposit data by ID using a custom hook
  const {
    deposit,
    error: errorDeposit,
    refetch: refetchDeposit,
    isLoading: loadingDeposit,
  } = useDepositById({
    id: depositId ? (parseToBigInt(depositId) as bigint) : undefined,
  });

  // Fetch withdrawal data by ID using a custom hook
  const {
    withdraw,
    error: errorWithdraw,
    refetch: refetchWithdraw,
    isLoading: loadingWithdraw,
  } = useWithdrawalById({
    id: withdrawId ? (parseToBigInt(withdrawId) as bigint) : undefined,
  });

  // Determine if there is an error in fetching either deposit or withdrawal data
  const error = errorDeposit ?? errorWithdraw;
  // Determine if either deposit or withdrawal data is still loading
  const isLoading = loadingDeposit ?? loadingWithdraw;

  /**
   * Function to refetch transaction data.
   * This function refetches deposit or withdrawal data if there was an error in fetching.
   *
   * Example usage:
   * refetch(); // Refetches the transaction data if there was an error
   */
  function refetch() {
    if (errorDeposit) {
      refetchDeposit();
    }

    if (errorWithdraw) {
      refetchWithdraw();
    }
  }

  // Return the error, refetch function, loading state, and transaction data
  return {
    error,
    refetch,
    isLoading,
    transaction: deposit ?? withdraw,
  };
}
